// speechBridge.js
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';                  // ★ 新增：用于生命周期监听
import { SpeechRecognition } from '@capacitor-community/speech-recognition';
import { createAzureAdapter } from './azureAdapter';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// 同一時間只允許一個語音控制器使用麥克風，避免搜尋與聊天視窗互相搶佔。
let activeSpeechController = null;

/** 依站內設定/環境自動挑選語音識別語言碼（BCP-47） */
function autoPickSpeechLang() {
  try {
    const st = localStorage.getItem('userLanguage'); // 'S' | 'T'
    if (st === 'T') return 'zh-TW';
    if (st === 'S') return 'zh-CN';
  } catch {}
  try {
    const htmlLang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
    if (/(hant|zh\-tw|zh\-hk|zh\-mo|yue)/.test(htmlLang)) return 'zh-TW';
    if (/(hans|zh\-cn|zh\-sg)/.test(htmlLang)) return 'zh-CN';
  } catch {}
  try {
    const langs = (navigator.languages?.length ? navigator.languages : [navigator.language || ''])
      .map((x) => String(x).toLowerCase());
    if (langs.some((l) => /(hant|zh\-tw|zh\-hk|zh\-mo|yue)/.test(l))) return 'zh-TW';
    if (langs.some((l) => /(hans|zh\-cn|zh\-sg)/.test(l))) return 'zh-CN';
  } catch {}
  return 'zh-CN';
}

export function createSpeechBridge({
  lang,
  onPartial,
  onFinal,
  onStateChange,
  afterGrantDelayMs = 200,
  startRetry = 1,
  maxInitialSilenceMs = 30000,
  maxIdleSilenceMs = 30000,
  webMaxAlternatives = 5,
  bindAppLifecycle = true,
  restartOnResume = true,
  holdUntilUserStops = true,
  // —— Azure 相关 —— //
  azure,                              // { region:'eastasia', tokenUrl:'/api/speech/token' }
  preferAzureOnAndroidChrome = true,
  forceAzure = false,                 // ★ 新增：作为参数而不是依赖外部变量
} = {}) {

  // 所有執行狀態必須屬於目前 bridge 實例。放在模組全域會令不同
  // 語音輸入元件互相覆蓋 starting/stopping/listener 狀態。
  let starting = false;
  let stopping = false;
  let lifecycleBound = false;
  let lifecycleHandle = null;
  let wasActiveBeforePause = false;
  let lastWebError = '';
  let consecutiveNoSpeech = 0;
  let nativeStarting = false;
  let nativeActive = false;
  let nativeRunId = 0;

  const emitState = (status, error = null) => {
    try { onStateChange?.({ status, error }); } catch {}
  };

  // 僅用 getUserMedia 做權限與裝置預檢，隨即釋放麥克風。
  // Android Chrome 若一直保留這條 stream，Web Speech 反而可能搶不到麥克風。
  async function checkWebMicrophone() {
    if (!globalThis.isSecureContext) throw new Error('insecure-context');
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('media-devices-unavailable');
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });
    try { stream.getTracks().forEach(track => track.stop()); } catch {}
  }

  async function safeNativeStop(timeoutMs = 500) {
    let isListening = nativeActive;
    try {
      const state = await SpeechRecognition.isListening();
      isListening = isListening || Boolean(state?.listening);
    } catch {}
    if (!isListening) return;
    try {
      await Promise.race([SpeechRecognition.stop(), sleep(timeoutMs)]);
    } catch {}
  }

  const resolvedLang = lang || autoPickSpeechLang();

  // —— 平台/环境判断 —— //
  const isWeb = !Capacitor.isNativePlatform();

  // 仅识别 Android Chrome（排除 Edge/Opera/SamsungBrowser 以及 WebView）
  const isAndroidChrome = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = String(navigator.userAgent || '');
    const isAndroid = /\bAndroid\b/i.test(ua);
    const isChrome  = /\bChrome\/\d+/.test(ua);
    const isEdge    = /\bEdg\//.test(ua);
    const isOpera   = /\bOPR\//.test(ua);
    const isSamsung = /\bSamsungBrowser\//.test(ua);
    const isWebView = /\bwv\b/.test(ua) || /\bVersion\/\d+/.test(ua);
    return Boolean(isAndroid && isChrome && !isEdge && !isOpera && !isSamsung && !isWebView);
  };
  const isAndroidApp = () =>
    Capacitor.isNativePlatform() && (Capacitor.getPlatform?.() === 'android');

  // Azure 配置
  const hasAzureConf = Boolean(azure && azure.region && azure.tokenUrl);
  const preferFlag = (preferAzureOnAndroidChrome === true);
  const forceFlag  = (typeof forceAzure === 'boolean' ? forceAzure : false);
  const onAC   = isAndroidChrome();  // Android Chrome（浏览器）
  const onAApp = isAndroidApp();     // Android App（不使用 Azure）

  // ✅ 只在 “Web && Android Chrome” 用 Azure
  const useAzureOnWeb = Boolean(isWeb && hasAzureConf && (forceFlag || (preferFlag && onAC)));

  // 调试
  // console.table({
  //   isWeb, hasAzureConf, forceFlag, preferFlag, onAC, onAApp, useAzureOnWeb,
  //   region: azure?.region, tokenUrl: azure?.tokenUrl,
  //   ua: (typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a')
  // });

  // 提前创建 Azure 适配器（仅在要用时）
  let azureAdapter = null;
  if (useAzureOnWeb) {
    azureAdapter = createAzureAdapter({
      ...azure,
      lang: resolvedLang,
      onPartial,
      onFinal,
    });
  }

  let webRec = null;
  let nativeSubs = [];
  const isNative = Capacitor.isNativePlatform();

  // 状态
  let active = false;
  let engineStarted = false;
  let startedAt = 0;
  let lastHeardAt = 0;
  let hasHeardAnything = false;

  // 去重
  let lastPartial = '';
  let lastFinalEmitted = '';

  const markHeard = () => { hasHeardAnything = true; lastHeardAt = Date.now(); };

  const shouldKeepAlive = () => {
    if (!active || !engineStarted) return false;
    if (holdUntilUserStops) return true;        // 用户不停止就一直续
    const now = Date.now();
    if (!hasHeardAnything) return now - startedAt < maxInitialSilenceMs;
    return now - lastHeardAt < maxIdleSilenceMs;
  };

  async function ensureNativePermission() {
    const { available } = await SpeechRecognition.available();
    if (!available) throw new Error('native-not-available');

    let perm = await SpeechRecognition.checkPermissions();
    if (perm?.speechRecognition === 'granted') return 'granted';

    await SpeechRecognition.requestPermissions();
    for (let i = 0; i < 3; i++) {
      perm = await SpeechRecognition.checkPermissions();
      if (perm?.speechRecognition === 'granted') return 'granted';
      await sleep(120);
    }
    return perm?.speechRecognition || 'denied';
  }

  async function startNativeLoop() {
    const state = await ensureNativePermission();
    if (state !== 'granted') throw new Error(`perm-not-granted:${state}`);

    await removeNativeListeners();
    if (nativeStarting || nativeActive) return;
    nativeStarting = true;
    const runId = ++nativeRunId;

    if (afterGrantDelayMs > 0) await sleep(afterGrantDelayMs);
    await safeNativeStop(500);
    await sleep(120);

    // partialResults=true 在 Android 外掛 7.0.1 會過早 resolve，之後的
    // onError 無法回到 JavaScript，畫面便永久停在「正在聆聽」。改用
    // 單次完整辨識，Promise 會在取得 matches 或發生錯誤時結束。
    nativeActive = true;
    engineStarted = true;
    startedAt = Date.now();
    emitState('listening');

    const recognition = SpeechRecognition.start({
      language: resolvedLang,
      maxResults: 5,
      partialResults: false,
      popup: false,
    });
    nativeStarting = false;

    Promise.resolve(recognition)
      .then(result => {
        if (runId !== nativeRunId || stopping || !active) return;
        const text = String(result?.matches?.[0] || '').trim();
        nativeActive = false;
        engineStarted = false;
        active = false;
        if (text) {
          markHeard();
          onFinal?.(text);
          emitState('idle');
        } else {
          emitState('error', new Error('native-no-match'));
        }
        if (activeSpeechController === controller) activeSpeechController = null;
        window.AppBridge?.micRelease?.();
      })
      .catch(error => {
        if (runId !== nativeRunId || stopping) return;
        nativeActive = false;
        engineStarted = false;
        active = false;
        if (activeSpeechController === controller) activeSpeechController = null;
        window.AppBridge?.micRelease?.();
        emitState('error', error);
      });
  }

  async function removeNativeListeners() {
    const listeners = nativeSubs;
    nativeSubs = [];
    await Promise.all(listeners.map(async listener => {
      try { await listener?.remove?.(); } catch {}
    }));
  }

  function startWebLoop() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) throw new Error('web-speech-not-available');

    const rec = new SR();
    webRec = rec;

    rec.lang = resolvedLang;
    rec.interimResults = true;
    rec.continuous = !onAC;                 // ★ 修正：用外层 onAC 布尔；安卓 Chrome 强制 false
    try { rec.maxAlternatives = webMaxAlternatives; } catch {}

    let restartTimer = null;
    const restartIfNeeded = () => {
      if (shouldKeepAlive() && lastWebError !== 'not-allowed') {
        clearTimeout(restartTimer);
        restartTimer = setTimeout(() => { try { rec.start(); } catch {} }, onAC ? 140 : 0);
      }
    };

    rec.onstart = () => {
      startedAt = Date.now();
      engineStarted = true;
      lastWebError = '';
      emitState('listening');
    };

    rec.onresult = (ev) => {
      let interim = ''; let finalTxt = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        const txt = r[0]?.transcript || '';
        if (r.isFinal) finalTxt += txt; else interim += txt;
      }
      if (interim) { lastPartial = interim; consecutiveNoSpeech = 0; markHeard(); onPartial?.(interim); }
      if (finalTxt) {
        markHeard();
        if (finalTxt !== lastPartial && finalTxt !== lastFinalEmitted) { onFinal?.(finalTxt); lastFinalEmitted = finalTxt; }
        else { onFinal?.(''); }
        lastPartial = '';
      }
    };

    rec.onerror = (e) => {
      lastWebError = e?.error || '';
      if (lastWebError === 'no-speech') {
        consecutiveNoSpeech++;
        if (consecutiveNoSpeech <= 3) setTimeout(restartIfNeeded, 200);
      } else if (lastWebError === 'network' || lastWebError === 'aborted') {
        setTimeout(restartIfNeeded, 220);
      } else {
        active = false;
        emitState('error', new Error(lastWebError || 'web-speech-error'));
      }
    };

    rec.onend = () => { onFinal?.(''); setTimeout(restartIfNeeded, onAC ? 140 : 120); };

    startedAt = Date.now();
    rec.start();
  }

  function bindLifecycleOnce(ctrl) {
    if (lifecycleBound || !bindAppLifecycle) return;
    lifecycleBound = true;
    try {
      const listenerPromise = App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          wasActiveBeforePause = active;
          ctrl.stop();
        } else if (wasActiveBeforePause && restartOnResume) {
          ctrl.start().catch(() => {});
          wasActiveBeforePause = false;
        }
      });
      Promise.resolve(listenerPromise).then(handle => { lifecycleHandle = handle; }).catch(() => {});
    } catch {}
  }

  const controller = {
    async start() {
      // 原生 App 不再提供語音輸入；網頁版仍使用 Web Speech／Azure。
      if (isNative) {
        const error = new Error('native-speech-disabled');
        emitState('error', error);
        throw error;
      }
      if (starting || active) return;
      starting = true;
      emitState('starting');
      try {
        if (activeSpeechController && activeSpeechController !== controller) {
          await activeSpeechController.stop();
        }
        activeSpeechController = controller;
        active = true; stopping = false;
        engineStarted = false; hasHeardAnything = false;
        lastHeardAt = 0; startedAt = Date.now();
        lastPartial = ''; lastFinalEmitted = ''; lastWebError = ''; consecutiveNoSpeech = 0;
        nativeStarting = false; nativeActive = false;

        bindLifecycleOnce(this);

        // Android App 可配合原生桥接保持焦点/亮屏
        window.AppBridge?.micAcquire?.();

        // 只在 Android Chrome（浏览器）尝试 Azure
        if (useAzureOnWeb) {
          try {
            await azureAdapter.start();
            engineStarted = true;
            emitState('listening');
            return;
          } catch (e) {
            console.warn('Azure start failed, fallback to WebSpeech:', e);
          }
        }

        // Web 端檢查權限後立即釋放預檢 stream，避免阻塞 Web Speech。
        if (!isNative) await checkWebMicrophone();

        // 原生优先，失败再回退 Web
        const tryWeb = () => { try { startWebLoop(); } catch (e2) { active = false; throw e2; } };
        if (isNative) {
          try { await startNativeLoop(); }
          catch (e) { console.warn('Native failed → fallback web:', e); tryWeb(); }
        } else {
          tryWeb();
        }
      } catch (error) {
        active = false;
        if (activeSpeechController === controller) activeSpeechController = null;
        window.AppBridge?.micRelease?.();
        emitState('error', error);
        throw error;
      } finally { starting = false; }
    },

    async stop() {
      if (stopping) return;
      stopping = true;
      try {
        active = false; engineStarted = false;
        nativeRunId++;

        if (useAzureOnWeb && azureAdapter) {
          try { await azureAdapter.stop(); } catch {}
        }

        // 只移除本 bridge 建立的 listener，不能清除其他語音元件的 listener。
        await safeNativeStop(400);
        await removeNativeListeners();
        nativeStarting = false; nativeActive = false;

        // Web 清理
        if (webRec) {
          try {
            webRec.onresult = webRec.onend = webRec.onerror = webRec.onstart = null;
            webRec.stop();
          } catch {}
          webRec = null;
        }
      } finally {
        if (activeSpeechController === controller) activeSpeechController = null;
        window.AppBridge?.micRelease?.();
        stopping = false;
        emitState('idle');
      }
    },

    async destroy() {
      await controller.stop();
      try { await lifecycleHandle?.remove?.(); } catch {}
      lifecycleHandle = null;
      lifecycleBound = false;
    },

    isNative() { return isNative; },
  };
  return controller;
}
